import { useEffect } from 'react';
import Layout from '../../../components/layout/Layout';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import AboutSideNav from '../../../components/about/AboutSideNav';
import { updateSEO, seoData } from '../../../utils/seo';

// ===== 理念と使命ページ 全面作り直し（Implementer C スコープ） =====
// デザイン土台: src/pages/about/overview/page.tsx（型の正典）と src/pages/home/page.tsx。
//   紺#1a2942 / 真鍮金#a38e65 / 金差し色見出し / リード3行+ / BTN-08 2状態 / AboutSideNav 配線。
//   overview の単純コピーはせず、理念ページに合う型を再設計（各セクションに由来注記）。
// 事実正典（創作禁止・出典path記録／外部実績主張の復活は禁止）:
//   [S1] mng-thebutler/00_事業説明.md L22（コンセプト＝おもてなし×西洋式執事の融合）
//   [S2] mng-thebutler/00_事業説明.md L25-27（三つの提供価値・先回りの奉仕＝理念の核）
//   [S3] 現行 about/mission/page.tsx 実文（使命4/展望4/三つの柱/約束6＝内部完結表現。外部提携・国際認証等の主張は復活させない）
//   [S4] overview データ行（135年 / 1890 / 3代＝overview 正典と一致）
// ここに無い年号・数値は書かない。

export default function MissionPage() {
  useEffect(() => {
    updateSEO(seoData['/about/mission']);
  }, []);

  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: '協会について', href: '/about' },
    { label: '理念と使命' }
  ];

  const aboutNav = [
    { label: '協会概要', href: '/about/overview' },
    { label: '理念と使命', href: '/about/mission' },
    { label: '沿革・歴史', href: '/about/history' },
    { label: '組織体制', href: '/about/organization' },
    { label: '主な活動', href: '/about/activities' },
    { label: 'アクセス・所在地', href: '/about/access' }
  ];

  // BTN-08（home / overview と同一文法・2状態）
  const BTN08_ON_NAVY = "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  // [S3] 私たちの使命（Mission・4項目）
  const missionItems = [
    { no: '01', en: 'Systematization', ja: '知識と技術の体系化', desc: '執事としての知識と技術を組織内で体系化し、高品質なサービスを安定して提供できる基盤を築きます。' },
    { no: '02', en: 'Expertise', ja: '専門性の向上', desc: '内部研修制度を通じて、一人ひとりの執事の専門性を継続的に高めていきます。' },
    { no: '03', en: 'Development', ja: '国際的な発展の促進', desc: '国内外での執事研修を通じて、日本ならではの執事文化の国際的な発展を促します。' },
    { no: '04', en: 'Society', ja: '社会への貢献', desc: '執事のプロフェッショナリズムと心構えを社会に広め、サービス業全体の質の向上に貢献します。' },
  ];

  // [S3] 私たちが描く未来（Vision・4項目）
  const visionItems = [
    { label: '社会的地位の確立', text: '執事が社会的に尊敬される専門職として確立され、多様なフィールドで活躍している。' },
    { label: '国際的な評価', text: '日本独自の文化と融合した伝統的な執事像が、国際的に評価されている。' },
    { label: '精神の浸透', text: '執事の精神が広く理解され、おもてなしの心が社会全体に浸透している。' },
    { label: 'グローバルネットワーク', text: '協会が執事文化の中心的な存在として、世界とつながるネットワークの一部となっている。' },
  ];

  // [S3] 活動の三つの柱
  const pillars = [
    {
      en: 'Development', ja: '人材育成',
      img: '/images/generated/jba-fs-012-about_mission.jpg', aspect: 'aspect-video',
      alt: '研修に取り組む執事たちの様子。技術と精神の両面を養う人材育成の現場',
      desc: '執事としての技術と精神の両面から、質の高いサービスを提供できる人材の育成に取り組んでいます。',
      items: ['体系的な研修プログラム', '実践的な技能訓練', 'メンターシップ制度', '継続的な学習支援'],
    },
    {
      en: 'Heritage', ja: '文化継承',
      img: '/images/generated/jba-fs-013-about_mission-v3.jpg', aspect: 'aspect-[3/4]', fit: 'object-cover',
      alt: '歴史ある邸宅と調度。135年にわたり受け継がれてきた執事文化の系譜',
      desc: '135年の歴史の中で培われた執事文化の伝統を守り、次の世代へと確かに継承していきます。',
      items: ['伝統技術の保存', '歴史資料の管理', '文化研究の推進', '出版・情報発信'],
    },
    {
      en: 'International', ja: '国際交流',
      img: '/images/generated/jba-fs-014-about_mission.jpg', aspect: 'aspect-video',
      alt: '海外からの賓客をもてなす執事。国際感覚を養う交流の場',
      desc: '海外からのお客様にも対応できる国際感覚を養い、グローバルな視点で執事文化の発展に貢献します。',
      items: ['国際感覚の育成', '異文化理解の推進', '海外文化の調査・研究', '国際基準の研究'],
    },
  ];

  // [S3] 私たちの約束（6項目）
  const promises = [
    { t: '最高品質のサービス提供', d: '常に最高品質のサービスを追求し、期待を超える価値をお届けします。' },
    { t: '継続的な成長と学習', d: '時代の変化に対応し、常に学び続ける姿勢を大切にします。' },
    { t: '倫理的な行動', d: '高い倫理観を持ち、誠実な行動を心がけます。' },
    { t: '伝統の尊重', d: '執事の伝統と格式を尊重し、その継承に努めます。' },
    { t: '国際的な協力', d: '世界の執事文化との交流を通じて、相互理解を深めます。' },
    { t: '社会への貢献', d: '執事の精神を社会に広め、より良い社会づくりに貢献します。' },
  ];

  return (
    <Layout>

      {/* ===== ヒーロー — 全幅ビルボード（画像+紺オーバーレイ+左寄せ）。
           由来: overview ヒーロー／カタログ BB-02（左寄せ打ち出し型）文法を理念ページへ適用 ===== */}
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[520px] lg:min-h-[600px] flex items-center">
        <img
          src="/images/generated/jba-fs-015-about_mission-v3.jpg"
          alt="静かに寄り添う執事のサービス。おもてなしの精神を体現する所作"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.92)_0%,rgba(16,20,26,0.72)_48%,rgba(16,20,26,0.35)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[680px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">Mission &amp; Philosophy</span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(28px,3.4vw,44px)] leading-[1.4] tracking-[0.04em] mb-6">
              <span className="block">見えないところに宿る、</span>
              <span className="block">最高のおもてなし</span>
            </h1>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
              全日本執事協会は、135年以上にわたり培われた日本の執事文化を礎に、「おもてなしの心」と「プロフェッショナリズム」を社会へと広げていくことを使命としています。私たちが目指すのは、相手が気づく前にその望みを叶える、静かで確かな奉仕のかたちです。
            </p>
          </div>
        </div>
      </section>
      {/* パンくず: ビルボード直後に配置（Yuichi 2026-07-17 指摘②）。共通Breadcrumbのヘッダー逃げpaddingを子セレクタで通常paddingへ上書き */}
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      {/* ===== 本文 = サイドナビ + コンテンツの2カラム ===== */}
      <div className="bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16 lg:py-20">
          <div className="grid lg:grid-cols-[248px_1fr] gap-8 lg:gap-14">

            {/* サイドナビ（共通コンポーネント・mission を強調） */}
            <AboutSideNav items={aboutNav} activeHref="/about/mission" />

            {/* コンテンツ列 */}
            <div className="min-w-0 space-y-16 sm:space-y-20">

              {/* --- I. 基本理念（リード+細罫データ行）。由来: overview S1 文法（金eyebrow+金差し見出し+3行リード+細罫データ行） --- */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">I. Our Philosophy</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
                  執事の真髄は、<span className="text-[#a38e65]">目に見えないところ</span>にある
                </h2>
                <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl">
                  <p>
                    全日本執事協会は、135年以上の歴史のなかで育まれてきた日本の執事文化を、より洗練された社会の礎とすることを目指しています。私たちが最も大切にするのは、執事が持つ「おもてなしの心」と「プロフェッショナリズム」という、目には見えない二つの価値です。
                  </p>
                  <p>
                    執事の仕事は、華やかに見せびらかすものではありません。相手が言葉にする前にその望みを察し、空気のように自然に動く。そうした「先回りの奉仕」こそが、私たちの追い求める最高のおもてなしのかたちです。
                  </p>
                  <p>
                    この理念を組織のなかで体系化し、次の世代へと確かに受け継いでいくこと。それが、日本で唯一の執事専門機関として私たちが担うべき役割だと考えています。
                  </p>
                </div>
                {/* 細罫データ行 */}
                <dl className="grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-4 mt-10 border-t border-[#e2ddd2] pt-8">
                  <div>
                    <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">創設</dt>
                    <dd className="font-['Playfair_Display',serif] text-2xl text-[#1a2942]">1890<span className="text-sm text-[#8f7a56] ml-1">年</span></dd>
                  </div>
                  <div>
                    <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">歴史</dt>
                    <dd className="font-['Playfair_Display',serif] text-2xl text-[#1a2942]">135<span className="text-sm text-[#8f7a56] ml-1">年</span></dd>
                  </div>
                  <div>
                    <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">使命</dt>
                    <dd className="font-['Noto_Serif_JP',serif] text-lg text-[#1a2942] leading-tight pt-1">文化の継承</dd>
                  </div>
                  <div>
                    <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">価値</dt>
                    <dd className="font-['Noto_Serif_JP',serif] text-lg text-[#1a2942] leading-tight pt-1">先回りの奉仕</dd>
                  </div>
                </dl>
              </section>

              {/* --- II. コンセプト大引用 = 新バリエーション①
                   由来注記: カタログ SEC-N01（フルブリード実写+紺暗幕+中央overlay）× SEC-N08（引用大組み）を合成した新造。
                   overview S3 の「素の紺パネル引用」とは別型（背景に実写を敷き、暗幕越しに理念を大きく組む）。 --- */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">II. Our Concept</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
                  日本のおもてなしと、西洋式執事の融合
                </h2>
                <div className="relative overflow-hidden">
                  <img
                    src="/images/generated/jba-hero-overview-01.jpg"
                    alt="暖炉と燭台のある執事サロンの一室"
                    className="absolute inset-0 w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(16,20,26,0.82)_0%,rgba(16,20,26,0.88)_100%)]"></div>
                  <div className="relative z-10 px-7 sm:px-12 lg:px-16 py-14 sm:py-16 lg:py-20">
                    <div className="text-[#d7b779] font-['Playfair_Display',serif] text-5xl leading-none mb-4">“</div>
                    <blockquote className="jba-display font-normal text-[#f8f6f0] text-xl sm:text-2xl lg:text-[30px] leading-[1.7] tracking-[0.02em] max-w-3xl">
                      主人が言葉にする前にニーズを察知し、空気のように自然に動く。この「先回りの奉仕」を、私たちは執事の本質と考えます。
                    </blockquote>
                    <div className="w-11 h-px bg-[#a38e65] mt-8 mb-6"></div>
                    <p className="font-['Noto_Serif_JP',serif] text-sm sm:text-base text-[#ded8cc] leading-[1.95] max-w-2xl">
                      英国式の規律と品格、そして日本独自の細やかな礼節。その二つが融合したとき、単なる家事の代行を超えた、暮らしとビジネスを包括的に支える執事のサービスが生まれます。
                    </p>
                  </div>
                </div>
              </section>

              {/* --- III. 私たちの使命 Mission（大番号エディトリアル2列・重要語は大フォント）。
                   由来: home 紺帯番号バンド（金番号+和見出し）+ カタログ SEC-N04（積層split）を、画像なしの太番号列へ翻案。
                   overview S4（番号付き2カラム・小字）とは字格・レイアウトを変える。 --- */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">III. Mission</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
                  私たちの<span className="text-[#a38e65]">4つの使命</span>
                </h2>
                <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mb-9">
                  理念を日々の活動へと落とし込むために、協会は4つの使命を掲げています。知識と技術を体系化し、専門性を高め、文化を国際的に発展させ、そして社会全体のサービスの質を引き上げる。この積み重ねが、執事という職業の価値を確かなものにしていきます。
                </p>
                <div className="grid md:grid-cols-2 gap-x-10 gap-y-8">
                  {missionItems.map((m) => (
                    <div key={m.no} className="flex gap-5 border-t border-[#e2ddd2] pt-6">
                      <span className="font-['Playfair_Display',serif] text-[#d7b779] text-4xl leading-none shrink-0 pt-1">{m.no}</span>
                      <div>
                        <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-[13px] tracking-[0.06em] mb-1">{m.en}</span>
                        <h3 className="jba-display font-medium text-[#1a2942] text-xl sm:text-[22px] mb-2.5 leading-[1.5]">{m.ja}</h3>
                        <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.9]">{m.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* --- IV. 私たちが描く未来 Vision（紺地・価値グリッド2×2・金差しラベル）。
                   由来: overview S3 の紺パネル内 value グリッド文法を、独立した「未来像」セクションへ展開（配色運用を変える）。 --- */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">IV. Vision</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
                  私たちが描く未来
                </h2>
                <div className="bg-[#1a2942] px-7 sm:px-10 lg:px-14 py-12 sm:py-14">
                  <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl mb-10">
                    使命の先に、私たちが思い描く執事の未来があります。執事が社会に尊敬される専門職となり、日本ならではの執事像が広く評価され、おもてなしの心が社会に根づく。その景色を、一歩ずつ確かに近づけていきます。
                  </p>
                  <div className="grid sm:grid-cols-2 gap-x-10 gap-y-9">
                    {visionItems.map((v, i) => (
                      <div key={v.label} className="border-t border-[#a38e65]/40 pt-5">
                        <div className="flex items-baseline gap-3 mb-2.5">
                          <span className="font-['Playfair_Display',serif] italic text-[#d7b779] text-sm">{`0${i + 1}`}</span>
                          <h3 className="jba-display font-medium text-[#f8f6f0] text-lg sm:text-xl">{v.label}</h3>
                        </div>
                        <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#ded8cc] leading-[1.9]">{v.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              {/* --- V. 活動の三つの柱（三列画像カード）。由来: カタログ SEC-N05（三列カードグリッド・中央見出し）。
                   各カードに実写のポイント画像を配し、柱ごとの取り組みをリストで構造化。 --- */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">V. Three Pillars</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
                  理念を支える、活動の三つの柱
                </h2>
                <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mb-9">
                  掲げた理念と使命は、日々の具体的な活動によって形になります。人を育てる「人材育成」、伝統を守り伝える「文化継承」、世界とつながる「国際交流」。この三つの柱が互いに支え合い、執事文化を前へと進めています。
                </p>
                <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
                  {pillars.map((p) => (
                    <div key={p.ja} className="border border-[#e2ddd2] bg-white flex flex-col">
                      <img src={p.img} alt={p.alt} className={`w-full h-48 ${(p as { fit?: string }).fit ?? 'object-cover'}`} />
                      <div className="p-6 sm:p-7 flex flex-col flex-1">
                        <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-[13px] tracking-[0.06em] mb-1">{p.en}</span>
                        <h3 className="jba-display font-medium text-[#1a2942] text-xl sm:text-[22px] mb-3 leading-[1.4]">{p.ja}</h3>
                        <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.9] mb-5">{p.desc}</p>
                        <ul className="mt-auto space-y-2 border-t border-[#e2ddd2] pt-4">
                          {p.items.map((it) => (
                            <li key={it} className="flex items-start gap-2.5 font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.7]">
                              <span className="text-[#a38e65] text-[10px] leading-none pt-1.5" aria-hidden="true">◆</span>
                              <span>{it}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* --- VI. 私たちの約束 = 新バリエーション②
                   由来注記: カタログ SEC-N05（三列カード）を、カード枠を廃した「井桁（十字細罫）グリッド」へ翻案した新造。
                   罫線で6項目を整然と区切り、カードの乱立を避けて一覧の可読性を高める。 --- */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">VI. Our Promise</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
                  私たちの約束
                </h2>
                <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mb-9">
                  理念・使命・未来像を実現するために、協会と執事は日々の行動においてこの6つを守り続けます。品質、成長、倫理、伝統、協力、そして社会への貢献。どれも派手さはありませんが、信頼はこうした約束の積み重ねからしか生まれません。
                </p>
                <div className="border-t border-l border-[#e2ddd2] grid sm:grid-cols-2 lg:grid-cols-3">
                  {promises.map((p, i) => (
                    <div key={p.t} className="border-r border-b border-[#e2ddd2] p-6 sm:p-7">
                      <div className="flex items-baseline gap-3 mb-2.5">
                        <span className="font-['Playfair_Display',serif] text-[#d7b779] text-lg">{`0${i + 1}`}</span>
                        <h3 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl leading-[1.4]">{p.t}</h3>
                      </div>
                      <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.9]">{p.d}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* --- VII. 締めCTA帯。由来: カタログ SEC-N12（締めCTA帯）／ overview S6 紺CTAパネル --- */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">VII. Next Steps</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
                  理念に共感いただける方へ
                </h2>
                <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
                  <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl mb-8">
                    執事文化の継承と発展という私たちの歩みに共感いただける方は、ぜひ協会の活動にご注目ください。資格制度や研修、そして執事人材サービスを通じて、その理念に触れていただけます。
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                    <a href="/qualifications" className={BTN08_ON_NAVY}>執事資格制度を見る</a>
                    <a href="/seminars" className={BTN08_ON_NAVY}>研修・セミナーを見る</a>
                    <a href="/contact" className={BTN08_ON_NAVY}>お問い合わせ</a>
                  </div>
                </div>
              </section>

            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
